import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import BackAnchor from "../../../../shared/components/BackAnchor";
import { addToGoogleDataLayer } from "../../../../shared/const/utls";
import ContinueCancelButtons from "../../../../shared/components/ContinueCancelButtons";
import SignatureCanvas from "react-signature-canvas";
import { Link } from "@mui/material";
import { setAppProperty } from "../../../../redux/actions";
import { isTouchDevice } from "../../../../helpers/utils";

const ConsentToShareInformationView = ({ t, i18n }) => {
  const applicantSignature = useSelector(
    (state) => state.app.applicantSignature
  );

  const otherAdultsInformation = useSelector(
    (state) => state.app.otherAdultsInformation
  );

  const APPLICANT_SIGNATURE_REDUX_APP_PROPERTY = "applicantSignature";
  const OTHER_APPLICANT_SIGNATURE_REDUX_APP_PROPERTY = "otherAdultsSignature";
  const [noSignError, setNoSignError] = useState(false);
  const [memberSignErrors, setMemberSignErrors] = useState({});

  const { personalInformation, questionDataList } = useSelector(
    (state) => state.app
  );

  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();

  const disableGApush = useMemo(() => {
    return JSON.parse(params.disableGA || false);
  }, [params]);

  const signatureRef = React.createRef(applicantSignature);
  const otherMembersSignRefs = React.useRef({});
  const heatingBillFiles = useSelector((state) => state.app.heatingBill);

  const handleApplicantSignMove = () => {
    if (memberSignErrors['applicant']) {
      setMemberSignErrors((prevErrors) => ({ ...prevErrors, applicant: false }));
    }
  };


  useEffect(() => {
    const canvas = signatureRef?.current?.getCanvas();
    if (isTouchDevice()) {
      canvas?.addEventListener("touchend", handleApplicantSignMove);
      return () => {
        canvas?.removeEventListener("touchend", handleApplicantSignMove);
      };
    } else {
      canvas?.addEventListener("mouseup", handleApplicantSignMove);
      return () => {
        canvas?.removeEventListener("mouseup", handleApplicantSignMove);
      };
    }
  }, [signatureRef]);

  const { firstName, lastName } = personalInformation;

  const withOtherMember =
    otherAdultsInformation?.length > 0 && otherAdultsInformation[0].firstName;

  const handleOtherSignMove = (localId) => {
    if (memberSignErrors[localId]) {
      setMemberSignErrors((prevErrors) => ({ ...prevErrors, [localId]: false }));
    }
  };


  useEffect(() => {
    const canvas = signatureRef?.current?.getCanvas();
    if (isTouchDevice()) {
      canvas?.addEventListener("touchend", handleApplicantSignMove);
    } else {
      canvas?.addEventListener("mouseup", handleApplicantSignMove);
    }

    if (withOtherMember && otherAdultsInformation?.length > 0) {
      for (const otherAdult of otherAdultsInformation) {
        const otherCanvas = otherMembersSignRefs.current[otherAdult.localId]?.getCanvas();
        if (isTouchDevice()) {
          otherCanvas?.addEventListener("touchend", () => handleOtherSignMove(otherAdult.localId));
        } else {
          otherCanvas?.addEventListener("mouseup", () => handleOtherSignMove(otherAdult.localId));
        }
      }
    }

    return () => {
      canvas?.removeEventListener("touchend", handleApplicantSignMove);
      canvas?.removeEventListener("mouseup", handleApplicantSignMove);
      if (withOtherMember && otherAdultsInformation?.length > 0) {
        for (const otherAdult of otherAdultsInformation) {
          const otherCanvas = otherMembersSignRefs.current[otherAdult.localId]?.getCanvas();
          otherCanvas?.removeEventListener("touchend", () => handleOtherSignMove(otherAdult.localId));
          otherCanvas?.removeEventListener("mouseup", () => handleOtherSignMove(otherAdult.localId));
        }
      }
    };
  }, [signatureRef, otherMembersSignRefs, otherAdultsInformation, withOtherMember]);



  const handleClear = () => {
    signatureRef.current?.clear();
  };

  const handleClearOther = (otherMemberLocalId) => {
    otherMembersSignRefs.current[otherMemberLocalId]?.clear();
  };

  useEffect(() => {
    if (!disableGApush) {
      try {
        addToGoogleDataLayer("Consent", "HARP Application", i18n.language);
        window.scrollTo(0, 0);
      } catch (e) {
        console.log(e);
      }
    }
  }, [i18n.language, disableGApush]);

  const onNext = () => {
    const newErrors = {};

    if (signatureRef?.current?.isEmpty()) {
      newErrors['applicant'] = true;
    }

    if (withOtherMember && otherMembersSignRefs.current) {
      for (const otherAdult of otherAdultsInformation) {
        if (otherMembersSignRefs.current[otherAdult.localId]?.isEmpty()) {
          newErrors[otherAdult.localId] = true;
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setMemberSignErrors(newErrors);
      return;
    }

    setMemberSignErrors({});

    if (signatureRef?.current?.isEmpty() === false) {
      const signatureImage = signatureRef.current.toDataURL();
      dispatch(setAppProperty(APPLICANT_SIGNATURE_REDUX_APP_PROPERTY, signatureImage));

      if (withOtherMember) {
        const membersSign = {};
        for (const otherAdult of otherAdultsInformation) {
          if (!otherMembersSignRefs.current[otherAdult.localId]?.isEmpty()) {
            const signatureImage = otherMembersSignRefs.current[otherAdult.localId].toDataURL();
            membersSign[otherAdult.localId] = signatureImage;
          }
        }
        if (Object.keys(membersSign).length > 0) {
          dispatch(setAppProperty(OTHER_APPLICANT_SIGNATURE_REDUX_APP_PROPERTY, membersSign));
        }
      }

      try {
        navigate(`/${i18n.language}${t("routes.review_summary")}`);
      } catch (e) {
        console.log(e);
      }
    }
  };

  const backPage = () => {
    if (heatingBillFiles?.length > 0) {
      return 'routes.upload_heating_bill'
    } else {
      return 'routes.without_uploading_bill'
    }
  }


  return (
    <div>
      <div className="container">
        <main>
          <article>
            <div className="row" style={{ paddingTop: "1em" }}></div>
            <BackAnchor backPath={backPage()} />
            <div className="row">
              <div className="col-xs-12 col-sm-8">
                <h1>
                  {withOtherMember
                    ? t("consent_to_share.header_multiple")
                    : t("consent_to_share.header_single")}
                </h1>
              </div>
              <div className="col-xs-12 col-sm-8 col-md-8">
                <div>
                  {withOtherMember
                    ? t("consent_to_share.para_multiple_1")
                    : t("consent_to_share.para_single_1")}
                </div>
                <br />
              </div>
              <div className="col-xs-12 col-sm-8 col-md-8">
                <div>
                  {withOtherMember
                    ? t("consent_to_share.para_multiple_2")
                    : t("consent_to_share.para_single_2")}
                </div>
              </div>

              <div className="col-xs-12 col-sm-8">
                <h3>
                  <b>{t("consent_to_share.how_to_sign")}</b>
                </h3>
              </div>
              <div className="col-xs-12 col-sm-8">
                <div className="" style={{ display: "flex" }}>
                  <img
                    className="image"
                    src={require("./../../../../assets/Touchpad.png")}
                    alt="Applicant's Sign"
                  />
                  <div>
                    <strong>{t("consent_to_share.touch_screen")}</strong>
                    <p>{t("consent_to_share.touch_screen_helper")}</p>
                  </div>
                </div>
                <div className="" style={{ display: "flex" }}>
                  <img
                    className="image"
                    src={require("./../../../../assets/ComputerMouse.png")}
                    alt="Other Applicant's Sign"
                  />
                  <div>
                    <strong>{t("consent_to_share.on_computer")}</strong>
                    <p>{t("consent_to_share.on_computer_helper")}</p>
                  </div>
                </div>
              </div>

              <div className="col-xs-12 col-sm-8" style={{ marginTop: "30px" }}>
                <h3>
                  {t("consent_to_share.signature_label")} {firstName} {lastName}
                </h3>
                <p>{t("consent_to_share.signature_label_helper")}</p>
                <div style={{ display: "flex" }}>
                  <div
                    className="col-xs-4"
                    style={{
                      background: "#EEEEEE",
                      width: "calc(100% - 70px)",
                      padding: 0,
                    }}
                  >
                    <SignatureCanvas
                      ref={signatureRef}
                      penColor="black"
                      backgroundColor="#EEEEEE"
                      canvasProps={{
                        style: { width: "100%", height: 100 },
                        className: "signature-canvas",
                      }}
                      clearOnResize={false}
                    />
                    <hr style={{ width: "80%", border: "solid 1px black" }} />
                  </div>
                  <div
                    className="col-xs-2"
                    style={{
                      height: "100%",
                      marginLeft: "10px",
                      paddingTop: "50px",
                    }}
                  >
                    <Link onClick={() => handleClear()}>{t("consent_to_share.clear")}</Link>
                  </div>
                </div>
                <div style={{ margin: '14px 0px' }}>
                  <span>{t("consent_to_share.clear_signature_helper")}</span>
                </div>

                {memberSignErrors['applicant'] && (
                  <div className="has-error" style={{ marginTop: "10px" }}>
                    <i
                      aria-label={t("errors.hidden_error_msg")}
                      className="fa fa-exclamation-circle error-indicator errorAlert"
                      aria-hidden="true"
                      style={{ marginRight: "10px" }}
                    />
                    <span>{t("errors.no_sign_error")}</span>
                  </div>
                )}
              </div>

              {withOtherMember &&
                otherAdultsInformation?.map((otherAdult) => (
                  <div className="col-xs-12 col-sm-8" key={otherAdult.localId}>
                    <h3>
                      {t("consent_to_share.signature_label")}{" "}
                      {otherAdult?.firstName + " " + otherAdult?.lastName}
                    </h3>
                    <p>{t("consent_to_share.other_signature_helper")}</p>
                    <div style={{ display: "flex" }}>
                      <div
                        className="col-xs-4"
                        style={{
                          background: "#EEEEEE",
                          width: "calc(100% - 70px)",
                          padding: 0,
                        }}
                      >
                        <SignatureCanvas
                          ref={(el) => (otherMembersSignRefs.current[otherAdult.localId] = el)}
                          penColor="black"
                          backgroundColor="#EEEEEE"
                          canvasProps={{ style: { width: "100%", height: 100 } }}
                          clearOnResize={false}
                        />
                        <hr style={{ width: "80%", border: "solid 1px black" }} />
                      </div>
                      <div
                        className="col-xs-2"
                        style={{
                          height: "100%",
                          marginLeft: "10px",
                          paddingTop: "50px",
                        }}
                      >
                        <Link onClick={() => handleClearOther(otherAdult.localId)}>
                          {t("consent_to_share.clear")}
                        </Link>
                      </div>
                    </div>
                    <div style={{ margin: '14px 0px' }}>
                      <span>{t("consent_to_share.clear_signature_helper")}</span>
                    </div>

                    {memberSignErrors[otherAdult.localId] && (
                      <div className="has-error" style={{ marginTop: "10px" }}>
                        <i
                          aria-label={t("errors.hidden_error_msg")}
                          className="fa fa-exclamation-circle error-indicator errorAlert"
                          aria-hidden="true"
                          style={{ marginRight: "10px" }}
                        />
                        <span>{t("errors.no_sign_error")}</span>
                      </div>
                    )}
                  </div>
                ))}


            </div>
            {noSignError && (
              <div className="has-error" style={{ marginTop: "10px" }}>
                <i
                  aria-label={t("errors.hidden_error_msg")}
                  className="fa fa-exclamation-circle error-indicator errorAlert"
                  aria-hidden="true"
                />
                <span className="ns-visually-hidden">
                  {t("errors.hidden_error_msg")}
                </span>
                <span style={{ marginLeft: "10px" }}>
                  {t("errors.no_sign_error")}
                </span>
              </div>
            )}
            <ContinueCancelButtons continueClick={onNext} />
          </article>
        </main>
      </div>
    </div>
  );
};

export default withTranslation("common")(ConsentToShareInformationView);
